/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import {
  Container, Box, Typography, Button, Grid, Paper, TextField,
  Select, MenuItem, FormControl, InputLabel, Chip, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, Card, CardContent, Stack
} from '@mui/material';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RestoreIcon from '@mui/icons-material/Restore';
import { format } from 'date-fns';
import { alpha, useTheme } from '@mui/material/styles';
import useIsMobile from '../hooks/useIsMobile';
import { MobileTable } from '../components/shared/MobileTable';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ReceiptIcon from '@mui/icons-material/Receipt';

function Tasks() {
  const theme = useTheme();
  const [tasks, setTasks] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    assignedTo: '',
    dateAssigned: new Date(),
    dueDate: new Date(),
    status: 'pending',
    completedDate: null,
    username: localStorage.getItem('username')
  });

  const isMobile = useIsMobile();

  useEffect(() => {
    fetchTasks();
  }, []);

  const fetchTasks = async () => {
    try {
      const username = localStorage.getItem('username');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/tasks?username=${username}`);
      const data = await response.json();
      if (response.ok) {
        setTasks(data.tasks);
      }
    } catch (error) {
      console.error('Error fetching tasks:', error);
    }
  };

  const handleAddTask = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/tasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newTask)
      });
      
      if (response.ok) {
        fetchTasks();
        setOpenDialog(false);
        resetForm();
      }
    } catch (error) {
      console.error('Error adding task:', error);
    }
  };

  const handleEditTask = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/tasks/${editingTask._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newTask)
      });
      
      if (response.ok) {
        fetchTasks();
        setOpenDialog(false);
        resetForm();
      }
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  const handleCompleteTask = async (taskId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/tasks/${taskId}/complete`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          status: 'completed',
          completedDate: new Date().toISOString()  // Format date as ISO string
        })
      });
      
      if (response.ok) {
        await fetchTasks();  // Make sure to await the fetch
      } else {
        console.error('Failed to complete task');
      }
    } catch (error) {
      console.error('Error completing task:', error);
    }
  };

  const handleReopenTask = async (taskId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/tasks/${taskId}/reopen`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          status: 'pending',
          completedDate: null
        })
      });
      
      if (response.ok) {
        await fetchTasks();
      } else {
        console.error('Failed to reopen task');
      }
    } catch (error) {
      console.error('Error reopening task:', error);
    }
  };

  const resetForm = () => {
    setNewTask({
      title: '',
      description: '',
      assignedTo: '',
      dateAssigned: new Date(),
      dueDate: new Date(),
      status: 'pending',
      completedDate: null,
      username: localStorage.getItem('username')
    });
    setEditingTask(null);
  };

  const handleOpenEdit = (task) => {
    setEditingTask(task);
    setNewTask({
      ...task,
      dateAssigned: new Date(task.dateAssigned),
      dueDate: task.dueDate ? new Date(task.dueDate) : new Date(),
    });
    setOpenDialog(true);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    try {
      return format(new Date(date), 'MM/dd/yy');
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const calculateDaysOverdue = (task) => {
    if (task.status === 'completed' || !task.dueDate) return 'N/A';
    
    try {
      const now = new Date();
      const dueDate = new Date(task.dueDate);
      const days = Math.round((now - dueDate) / (1000 * 60 * 60 * 24));
      return days <= 0 ? '0' : `${days} days`;
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const formatStatus = (status) => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const filteredTasks = tasks.filter(task => {
    if (!showHistory && task.status !== 'pending') return false;
    
    if (searchTerm === '') return true;
    
    const searchLower = searchTerm.toLowerCase();
    return (
      task.title?.toLowerCase().includes(searchLower) ||
      task.description?.toLowerCase().includes(searchLower) ||
      task.assignedTo?.toLowerCase().includes(searchLower) ||
      formatDate(task.dateAssigned).toLowerCase().includes(searchLower) ||
      formatDate(task.dueDate).toLowerCase().includes(searchLower) ||
      task.status?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
      <Box sx={{ mt: 4, mb: 4 }}>
        <Grid container spacing={{ xs: 1, sm: 2, md: 3 }} alignItems="center" justifyContent="space-between">
          <Grid item xs={12} sm={8}>
            <Typography variant="h4" component="h1" gutterBottom>
              Tasks Management
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4} sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                resetForm();
                setOpenDialog(true);
              }}
              size="small"
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              Add New Task
            </Button>
          </Grid>
        </Grid>

        <Box sx={{ mt: 2, mb: 2 }}>
          <Grid container spacing={{ xs: 1, sm: 2 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Search Tasks"
                placeholder="Search in all fields..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Button
                variant="outlined"
                onClick={() => setShowHistory(!showHistory)}
                size="small"
                sx={{ width: { xs: '100%', sm: 'auto' } }}
              >
                {showHistory ? 'Show Active Tasks' : 'Show Task History'}
              </Button>
            </Grid>
          </Grid>
        </Box>

        {isMobile ? (
          <MobileTable
            data={filteredTasks}
            columns={[
              { field: 'title', label: 'Category' },
              { field: 'description', label: 'Description' },
              { field: 'assignedTo', label: 'Assigned To' },
              { field: 'dueDate', label: 'Due Date', format: formatDate },
              { field: 'status', label: 'Status', format: formatStatus }
            ]}
            actions={(task) => (
              <>
                {task.status === 'pending' ? (
                  <>
                    <IconButton size="small" onClick={() => handleCompleteTask(task._id)} sx={{ p: 0.5 }}>
                      <CheckCircleIcon color="success" fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleOpenEdit(task)} sx={{ p: 0.5 }}>
                      <EditIcon color="primary" fontSize="small" />
                    </IconButton>
                  </>
                ) : (
                  <IconButton 
                    size="small"
                    onClick={() => handleReopenTask(task._id)}
                    sx={{ p: 0.5 }}
                  >
                    <RestoreIcon color="primary" fontSize="small" />
                  </IconButton>
                )}
              </>
            )}
          />
        ) : (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ px: { xs: 1, sm: 2 } }}>Category</TableCell>
                  <TableCell sx={{ px: { xs: 1, sm: 2 } }}>Description</TableCell>
                  <TableCell sx={{ px: { xs: 1, sm: 2 } }}>Assigned To</TableCell>
                  <TableCell sx={{ px: { xs: 1, sm: 2 } }}>Date Assigned</TableCell>
                  <TableCell sx={{ px: { xs: 1, sm: 2 } }}>Due Date</TableCell>
                  <TableCell sx={{ px: { xs: 1, sm: 2 } }}>Status</TableCell>
                  <TableCell sx={{ px: { xs: 1, sm: 2 } }}>Overdue</TableCell>
                  <TableCell sx={{ px: { xs: 1, sm: 2 } }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredTasks.map(task => (
                  <TableRow
                    key={task._id}
                    sx={{
                      '&:last-child td, &:last-child th': { border: 0 },
                      '&:hover': { backgroundColor: alpha(theme.palette.action.hover, 0.04) }
                    }}
                  >
                    <TableCell sx={{ px: { xs: 1, sm: 2 }, py: 2 }}>{task.title}</TableCell>
                    <TableCell sx={{
                      px: { xs: 1, sm: 2 },
                      py: 2,
                      whiteSpace: 'normal',
                      wordBreak: 'break-word'
                    }}>
                      {task.description}
                    </TableCell>
                    <TableCell sx={{ px: { xs: 1, sm: 2 }, py: 2 }}>{task.assignedTo}</TableCell>
                    <TableCell sx={{ px: { xs: 1, sm: 2 }, py: 2 }}>{formatDate(task.dateAssigned)}</TableCell>
                    <TableCell sx={{ px: { xs: 1, sm: 2 }, py: 2 }}>{formatDate(task.dueDate)}</TableCell>
                    <TableCell sx={{ px: { xs: 1, sm: 2 }, py: 2 }}>{formatStatus(task.status)}</TableCell>
                    <TableCell sx={{ px: { xs: 1, sm: 2 }, py: 2 }}>{calculateDaysOverdue(task)}</TableCell>
                    <TableCell sx={{ px: { xs: 0, sm: 1 }, py: 2 }}>
                      {task.status === 'pending' ? (
                        <>
                          <IconButton onClick={() => handleCompleteTask(task._id)} size="small" sx={{ p: 0.5 }}>
                            <CheckCircleIcon color="success" fontSize="small" />
                          </IconButton>
                          <IconButton onClick={() => handleOpenEdit(task)} size="small" sx={{ p: 0.5 }}>
                            <EditIcon color="primary" fontSize="small" />
                          </IconButton>
                        </>
                      ) : (
                        <IconButton 
                          onClick={() => handleReopenTask(task._id)}
                          title="Reopen Task"
                          size="small"
                          sx={{ p: 0.5 }}
                        >
                          <RestoreIcon color="primary" fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      <Dialog 
        open={openDialog} 
        onClose={() => setOpenDialog(false)} 
        maxWidth="md" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ px: isMobile ? 2 : 3 }}>
          {editingTask ? 'Edit Task' : 'Add New Task'}
        </DialogTitle>
        <DialogContent sx={{ px: isMobile ? 2 : 3 }}>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Category"
              value={newTask.title}
              onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
              size="small"
            />
            <TextField
              fullWidth
              label="Description"
              multiline
              rows={4}
              value={newTask.description}
              onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
              size="small"
            />
            <TextField
              fullWidth
              label="Assign To"
              value={newTask.assignedTo}
              onChange={(e) => setNewTask({ ...newTask, assignedTo: e.target.value })}
              size="small"
            />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  selected={newTask.dateAssigned}
                  onChange={(date) => setNewTask({ ...newTask, dateAssigned: date })}
                  customInput={<TextField fullWidth label="Date Assigned" size="small" />}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  selected={newTask.dueDate}
                  onChange={(date) => setNewTask({ ...newTask, dueDate: date })}
                  customInput={<TextField fullWidth label="Due Date" size="small" />}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: isMobile ? 2 : 3, pb: isMobile ? 2 : 3 }}>
          <Button onClick={() => setOpenDialog(false)} size="small">Cancel</Button>
          <Button 
            onClick={editingTask ? handleEditTask : handleAddTask} 
            variant="contained"
            size="small"
          >
            {editingTask ? 'Update Task' : 'Add Task'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default Tasks;
